/**
 * features/letters/LettersManager.tsx
 * Phase 4 — Company Documents. Archive of generated regulatory
 * letters with download / email / delete, plus a New-letter flow.
 */
import { useMemo, useState } from 'react';
import { ScrollText, Plus, Search, Download, Mail, Trash2 } from 'lucide-react';
import { useLetters } from './useLetters';
import { LetterEditor } from './LetterEditor';
import { useSettings } from '@/contexts/SettingsContext';
import { downloadLetterPdf, letterPdfBase64, letterFileName } from '@/lib/letterPdf';
import { SendDocumentModal } from '@/features/common/SendDocumentModal';
import { DEFAULT_COMPANY_SETTINGS, type CompanyDocument, type LetterKind } from '@/types';
import { deleteLetter } from './lettersWrite';
import { Button, Card, Input, Badge, Spinner } from '@/components/ui/primitives';

const KIND_LABEL: Record<LetterKind, string> = {
  account_opening: 'Account opening',
  employee_confirmation: 'Employee confirmation',
  board_resolution: 'Board resolution',
  general: 'General letter',
};
function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function LettersManager() {
  const { letters, loading } = useLetters();
  const { settings } = useSettings();
  const company = settings ?? DEFAULT_COMPANY_SETTINGS;
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [emailDoc, setEmailDoc] = useState<CompanyDocument | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return letters.filter(
      (l) =>
        !q ||
        l.reference.toLowerCase().includes(q) ||
        l.subject.toLowerCase().includes(q) ||
        l.recipient.toLowerCase().includes(q),
    );
  }, [letters, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by reference, subject, or recipient…" className="pl-9" />
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setNewOpen(true)}>
          New letter
        </Button>
      </div>

      {loading ? (
        <Spinner label="Loading documents…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <ScrollText size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search ? 'No documents match.' : 'No company letters yet — create your first.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Reference</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Subject</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => (
                <tr key={l.id} className="border-b border-black/5 last:border-0 dark:border-white/5">
                  <td className="px-4 py-3 font-semibold text-primary dark:text-gray-100">{l.reference}</td>
                  <td className="px-4 py-3">
                    <Badge tone="neutral">{KIND_LABEL[l.kind]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{l.subject}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(l.date)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => downloadLetterPdf(l, company)} title="Download PDF" className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5">
                        <Download size={16} />
                      </button>
                      <button onClick={() => setEmailDoc(l)} title="Email" className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5">
                        <Mail size={16} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete ${l.reference} from the archive?`)) void deleteLetter(l.id);
                        }}
                        title="Delete"
                        className="rounded-lg p-1.5 text-primaryLight transition hover:bg-red-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-white/5"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {newOpen && <LetterEditor onClose={() => setNewOpen(false)} />}

      {emailDoc && (
        <SendDocumentModal
          heading={`Email ${emailDoc.reference}`}
          to=""
          subject={`${emailDoc.subject} — ${company.tradingName || company.legalName}`}
          message={`Good day,\n\nPlease find attached our letter (ref ${emailDoc.reference}).\n\nKind regards,\n${company.tradingName || company.legalName}`}
          filename={letterFileName(emailDoc)}
          contentBase64={letterPdfBase64(emailDoc, company)}
          relatedEntityType="companyDocument"
          relatedEntityId={emailDoc.id}
          onClose={() => setEmailDoc(null)}
        />
      )}
    </div>
  );
}
